import * as React from 'react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@xanectech/ui/components';

import {
  useFloating,
  offset,
  flip,
  shift,
  Placement,
} from '@floating-ui/react-dom';
import { useIsMobile } from '@xanectech/ui/lib';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
} from '@xanectech/ui/components';

interface TooltipWrapperProps extends React.PropsWithChildren {
  /** The tooltip text to display */
  tooltip: string;
  /** Optional additional class names for the tooltip content */
  className?: string;
  /** Optional: Props for the trigger element */
  triggerProps?: React.HTMLAttributes<HTMLElement>;
  /** Optional: Props for the tooltip content */
  contentProps?: React.ComponentPropsWithoutRef<typeof TooltipContent>;
  /** Optional: Side offset for the tooltip content */
  sideOffset?: number;
  /** Optional: Side for the tooltip content */
  side?: Placement;
  label?: string;
}

/**
 * TooltipWrapper wraps any element and displays a tooltip on hover/focus.
 *
 * @example
 * <TooltipWrapper tooltip="This is a tooltip">
 *   <button>Hover me</button>
 * </TooltipWrapper>
 */
const TooltipWrapper: React.FC<TooltipWrapperProps> = ({
  tooltip,
  children,
  className,
  triggerProps,
  contentProps,
  side = 'top',
  sideOffset = 4,
  label,
}) => {
  const isMobile = useIsMobile();

  const { refs, floatingStyles, placement } = useFloating({
    placement: side,
    middleware: [
      // offset({ mainAxis: sideOffset, crossAxis: 0, alignmentAxis: 0 }),
      offset(({ rects }) => {
        return -rects.reference.height / 2 - rects.floating.height / 2;
      }),
      flip(),
      shift(),
    ],
  });

  if (isMobile) {
    return (
      <Dialog>
        <DialogOverlay className='bg-gradient-glass' />
        <DialogTrigger asChild>
          <button type='button' className='cursor-pointer'>
            {children}
          </button>
        </DialogTrigger>
        <DialogContent className='sm:max-w-md px-4'>
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>{tooltip}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild {...triggerProps}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        ref={refs.setFloating}
        style={floatingStyles}
        className={`max-w-lg w-40 whitespace-normal break-words px-3 py-2 rounded-md shadow-lg bg-gradient-glass text-foreground text-sm ${
          className ?? ''
        }`}
        sideOffset={sideOffset}
        {...contentProps}
        data-placement={placement}
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

TooltipWrapper.displayName = 'TooltipWrapper';

export { TooltipWrapper, type TooltipWrapperProps };
